import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import moment from "moment";
import ItemService from "../../../../services/supplier_appCommonService";
import { formatDateTime } from "../../../../common/Validations";
import { InputField } from "../../../../Components/formElements/InputField";

const ExtendClosingTimeModal = ({ show, onHide, formInputs, onSuccess }) => {
  const [newClosingDateTime, setNewClosingDateTime] = useState("");
  const [modalRemainingSeconds, setModalRemainingSeconds] = useState(0);

  // Live countdown timer
  useEffect(() => {
    if (!show || !newClosingDateTime) {
      setModalRemainingSeconds(0);
      return;
    }

    const updateRemaining = () => {
      const now = moment();
      const closing = moment(newClosingDateTime, "YYYY-MM-DDTHH:mm");
      const diffSec = Math.max(0, Math.floor(closing.diff(now, "seconds")));
      setModalRemainingSeconds(diffSec);
    };

    updateRemaining();
    const interval = setInterval(updateRemaining, 1000);
    return () => clearInterval(interval);
  }, [show, newClosingDateTime]);

  // Pre-fill with current closing when modal opens
  useEffect(() => {
    if (show && formInputs?.closingDateTime) {
      const currClosing = moment(
        formInputs.closingDateTime,
        "YYYY-MM-DD HH:mm",
      );
      setNewClosingDateTime(currClosing.format("YYYY-MM-DDTHH:mm"));
    }
  }, [show, formInputs]);

  const handleExtendSubmit = async () => {
    if (!newClosingDateTime) {
      toast.error("Please select a new closing date & time", {
        position: "top-right",
      });
      return;
    }

    const openingDt = moment(formInputs.openingDateTime, "YYYY-MM-DD HH:mm");
    const closingDt = moment(newClosingDateTime, "YYYY-MM-DDTHH:mm");

    if (closingDt.isSameOrBefore(openingDt)) {
      toast.error("Closing date must be after opening date", {
        position: "top-right",
      });
      return;
    }

    const diffHours = closingDt.diff(openingDt, "hours", true);
    const totalHour = Math.round(diffHours * 100) / 100;

    const payload = {
      businessComponentInstanceId: formInputs?.rfxNr,
      closingDateTime: closingDt.format("YYYY-MM-DD HH:mm"),
      totalHour,
    };

    const toastId = toast.loading("Updating...", { position: "top-right" });
    try {
      const res = await ItemService.updateRfx(payload);
      if (res?.status === 200) {
        toast.success(res?.message || "Time extended successfully", {
          position: "top-right",
        });
        onHide();
        if (onSuccess) onSuccess();
      } else {
        toast.error(res?.message || "Failed to update time", {
          position: "top-right",
        });
      }
    } catch (err) {
      toast.error("Something went wrong", { position: "top-right" });
    }
    toast.dismiss(toastId);
  };

  const totalHours = newClosingDateTime
    ? Math.round(
        moment(newClosingDateTime, "YYYY-MM-DDTHH:mm").diff(
          moment(formInputs?.openingDateTime, "YYYY-MM-DD HH:mm"),
          "hours",
          true,
        ) * 100,
      ) / 100
    : 0;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Update RFQ Timer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col
            xs={12}
            md={6}
            lg={6}
            className="commTopButtonRightLeftPadding mb-3"
          >
            <InputField
            className="inputBox"
              label="Opening Date & Time"
              value={formatDateTime(formInputs?.openingDateTime) || ""}
              disabled={true}
            />
          </Col>
          <Col
            xs={12}
            md={6}
            lg={6}
            className="commTopButtonRightLeftPadding mb-3"
          >
            <InputField
            className="inputBox"
              label="Current Closing Date & Time"
              value={formatDateTime(formInputs?.closingDateTime) || ""}
              disabled={true}
            />
          </Col>
        </Row>
        <Row>
          <Col
            xs={12}
            md={6}
            lg={6}
            className="commTopButtonRightLeftPadding mb-3"
          >
            <label className="form-label">
              New Closing Date & Time <span className="text-danger">*</span>
            </label>
            <input
              type="datetime-local"
              className="form-control"
              value={newClosingDateTime}
              onChange={(e) => setNewClosingDateTime(e.target.value)}
            />
          </Col>
          <Col
            xs={12}
            md={6}
            lg={6}
            className="commTopButtonRightLeftPadding mb-3 d-flex align-items-end"
          >
            <div className="w-100">
              <InputField
              className="inputBox"
                label="Total Hours"
                value={totalHours}
                disabled={true}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col
            xs={12}
            md={12}
            lg={12}
            className="commTopButtonRightLeftPadding"
          >
            <div className="w-100 p-3 border rounded bg-light">
              <p className="fw-bold mb-2 text-center">
                Remaining Time After Update
              </p>
              <div className="d-flex justify-content-center gap-3 fs-4">
                <div className="text-center">
                  <span className="fw-bold">
                    {String(Math.floor(modalRemainingSeconds / 86400)).padStart(
                      2,
                      "0",
                    )}
                  </span>
                  <div className="small text-muted">Days</div>
                </div>
                <span className="fw-bold">:</span>
                <div className="text-center">
                  <span className="fw-bold">
                    {String(
                      Math.floor((modalRemainingSeconds % 86400) / 3600),
                    ).padStart(2, "0")}
                  </span>
                  <div className="small text-muted">Hours</div>
                </div>
                <span className="fw-bold">:</span>
                <div className="text-center">
                  <span className="fw-bold">
                    {String(
                      Math.floor((modalRemainingSeconds % 3600) / 60),
                    ).padStart(2, "0")}
                  </span>
                  <div className="small text-muted">Mins</div>
                </div>
                <span className="fw-bold">:</span>
                <div className="text-center">
                  <span className="fw-bold">
                    {String(modalRemainingSeconds % 60).padStart(2, "0")}
                  </span>
                  <div className="small text-muted">Secs</div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Row className="w-100">
          <Col sm={4} md={4} lg={4} xl={4}>   
        <button className="btnTable btn-danger" onClick={onHide}>
          <i className="bi bi-x-circle me-1"></i> Cancel
        </button>
          </Col >
          
        <Col sm={4} md={4} lg={4} xl={4}>
        <button
          className="btnTable"
          onClick={() => {
            const now = moment();
            setNewClosingDateTime(now.format("YYYY-MM-DDTHH:mm"));
          }}
        >
          <i className="bi bi-clock me-1"></i> Close Now
        </button>
        </Col>
        <Col sm={4} md={4} lg={4} xl={4}>
        <button className="btnTable" onClick={handleExtendSubmit}>
          <i className="bi bi-check-circle me-1"></i> Update
        </button>
        </Col>
</Row>
      </Modal.Footer>
    </Modal>
  );
};

export default ExtendClosingTimeModal;
